import { useCallback, useState } from 'react';
import { ArrowPathIcon, PauseCircleIcon } from '@heroicons/react/24/outline';
import { useQueryClient, type QueryClient } from '@tanstack/react-query';
import { useCase, useMeta } from '../../../hooks/useWorkQueue';
import { DM_MESSAGES_INBOX_KEY, DM_NOTIFICATIONS_KEY } from '../../../hooks/queryKeys';
import { useStatusCatalog } from '../../../services/statusCatalog';
import type { WorkQueueCase } from '../../../services/workQueue';
import { RefinementLandingPage } from '../../patient-files/actions/vca/refinement/RefinementLandingPage';
import { RetentionLandingPage } from '../../patient-files/actions/vca/retention/RetentionLandingPage';
import { ActionCard } from '../../patient-files/actions/ActionCard';
import { PutOnHoldModal } from '../../work-queue/PutOnHoldModal';
import { useTaskFlowParam } from '../../work-queue/tasks/useTaskFlowParam';
import { invalidateCaseDetail } from '../../work-queue/util';
import { CASE_ACTION_FLOWS, type CaseActionFlow } from './caseActionFlow';

const FLOW_PAGES: Record<CaseActionFlow, typeof RefinementLandingPage> = {
    refinement: RefinementLandingPage,
    retention: RetentionLandingPage
};

const HOLD_BLOCKED_BY_MACRO: Partial<Record<string, string>> = {
    hold: 'Already on hold',
    terminal: 'Case is closed'
};
const CASE_STATUS_UNAVAILABLE = 'Case status unavailable';
const CASE_LOADING = 'Loading case…';

interface HoldGate {
    workCase: WorkQueueCase | undefined;
    disabledReason: string | undefined;
}

// Same gate as the work-queue DetailPanel: no hold from a hold or terminal status.
function useHoldGate(patientId: string): HoldGate {
    const caseQuery = useCase(patientId);
    const metaQuery = useMeta();
    const catalog = useStatusCatalog();
    const workCase = caseQuery.data;
    if (caseQuery.isPending || metaQuery.isPending) {
        return { workCase: undefined, disabledReason: CASE_LOADING };
    }
    const macro = workCase ? catalog.meta(workCase.statusId)?.macro : undefined;
    return {
        workCase,
        disabledReason: macro ? HOLD_BLOCKED_BY_MACRO[macro] : CASE_STATUS_UNAVAILABLE
    };
}

function invalidateMonitoring(queryClient: QueryClient): Promise<unknown> {
    return Promise.all([
        queryClient.invalidateQueries({ queryKey: [DM_NOTIFICATIONS_KEY] }),
        queryClient.invalidateQueries({ queryKey: [DM_MESSAGES_INBOX_KEY] })
    ]);
}

interface CaseActionMenuProps {
    holdDisabledReason: string | undefined;
    onOpenFlow: (flow: CaseActionFlow) => void;
    onHold: () => void;
}

function CaseActionMenu({ holdDisabledReason, onOpenFlow, onHold }: CaseActionMenuProps) {
    return (
        <div className="rounded-xl border border-gray-200 bg-white p-4 flex flex-col gap-3">
            <div className="flex flex-wrap gap-3">
                <ActionCard
                    icon={ArrowPathIcon}
                    label="Trigger refinement"
                    onSelect={() => onOpenFlow('refinement')}
                />
                <ActionCard
                    icon={ArrowPathIcon}
                    label="Trigger retention"
                    onSelect={() => onOpenFlow('retention')}
                />
                <ActionCard
                    icon={PauseCircleIcon}
                    label="Put on hold"
                    disabled={Boolean(holdDisabledReason)}
                    disabledReason={holdDisabledReason}
                    onSelect={onHold}
                />
            </div>
            {holdDisabledReason && (
                <p className="text-xs text-gray-500">Put on hold: {holdDisabledReason}</p>
            )}
        </div>
    );
}

// Trigger refinement or retention (the wizard fills the tab, in the URL) or put the case on hold.
export function CaseActionsTab({ patientId }: { patientId: string }) {
    const queryClient = useQueryClient();
    const { flow, open: openFlow, close: closeFlow } = useTaskFlowParam(CASE_ACTION_FLOWS);
    const [holdOpen, setHoldOpen] = useState(false);
    const hold = useHoldGate(patientId);

    const handleTriggered = () => {
        closeFlow();
        void Promise.all([
            invalidateCaseDetail(queryClient, patientId),
            invalidateMonitoring(queryClient)
        ]);
    };
    // PutOnHoldModal invalidates the case detail itself and reports no outcome, so refresh on close.
    const handleHoldClosed = useCallback(() => {
        setHoldOpen(false);
        void invalidateMonitoring(queryClient);
    }, [queryClient]);

    if (flow) {
        const LandingPage = FLOW_PAGES[flow];
        return (
            <div className="flex-1 min-h-0 flex flex-col">
                <LandingPage
                    patientId={patientId}
                    patientName={hold.workCase?.name ?? ''}
                    onBack={closeFlow}
                    onSubmitted={handleTriggered}
                />
            </div>
        );
    }

    return (
        <div className="flex flex-col p-4">
            <CaseActionMenu
                holdDisabledReason={hold.disabledReason}
                onOpenFlow={openFlow}
                onHold={() => setHoldOpen(true)}
            />
            {holdOpen && hold.workCase && (
                <PutOnHoldModal workCase={hold.workCase} onClose={handleHoldClosed} />
            )}
        </div>
    );
}
